// The saved comparison of today's prices and the offers (Lane 1331, Phase 2
// slice 2C). Every figure is the saved record's own; the browser formats it
// and never adds, subtracts or recomputes a total. A comparison whose inputs
// changed says so and offers a rebuild; it is never recomputed silently.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pager } from '../../components/ui/DataPrimitives';
import { formatDueAt, formatVolume } from '../sourcing-portal/portalView';
import { formatAmount, formatOwnPrice, formatUnitPrice } from './sourcingApi';

const PAGE_SIZE = 100;
const COVERAGE_ROWS = [
  ['received', 'Received an offer', 'lines_received'],
  ['approved', 'With an approved offer', 'lines_approved'],
  ['conditional_only', 'Conditional offers only', 'lines_conditional_only'],
  ['min_order_only', 'Only offers with a minimum order above the volume', 'lines_min_order_only'],
  // Lane 1354: a line the calculation does not take (unit or buying step) is not priced.
  ['not_calculated', 'Not calculated: check the quantity and unit', 'lines_not_calculated'],
  ['no_offer', 'No offer', 'lines_no_offer'],
];

// Quantity per unit, the server's value only reformatted for reading (no
// arithmetic); units are never added together.
function quantityText(quantity) {
  const entries = Object.entries(quantity || {});
  if (entries.length === 0) return 'Not available';
  return entries.map(([unit, qty]) => `${formatVolume(qty)} ${unit}`).join('; ');
}

const lineWord = (n) => `${Number(n || 0).toLocaleString()} ${Number(n) === 1 ? 'line' : 'lines'}`;

function Coverage({ summary, compact }) {
  const cov = summary.coverage;
  const n = (v) => Number(v || 0).toLocaleString();
  if (!cov) {
    // An older record without the per-row detail: its counts only.
    return (
      <ul className="srce-plainlist">
        {COVERAGE_ROWS.map(([key, text, count]) => (summary[count] === undefined ? null
          : <li key={key}>{text}: {n(summary[count])} lines</li>))}
      </ul>
    );
  }
  return (
    <div className={`srce-cov${compact ? ' srce-cov--compact' : ''}`}>
      {!compact && (
        <div className="srce-cov-row srce-cov-head">
          <span>Lines</span><span className="srce-r">Count</span><span>Volume</span><span className="srce-r">Value at today's price</span>
        </div>
      )}
      {COVERAGE_ROWS.map(([key, text]) => {
        const c = cov[key];
        if (!c) return null;
        return (
          <div key={key} className="srce-cov-row">
            <span>{text}</span>
            <span className="srce-r srce-num">{n(c.lines)}{compact ? ' lines' : ''}</span>
            <span className="srce-num">{quantityText(c.quantity)}</span>
            <span className="srce-r srce-num">
              {formatAmount(c.baseline_value, summary.currency)}
              {Number(c.lines_without_usable_price || 0) > 0 && (
                <div className="srce-warn">
                  {n(c.lines_without_usable_price)} {Number(c.lines_without_usable_price) === 1 ? 'line has' : 'lines have'} no
                  {' '}usable price today and {Number(c.lines_without_usable_price) === 1 ? 'is' : 'are'} not in this value
                </div>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function MoneyCard({ title, figures, currency }) {
  return (
    <div className="srce-cmp-card">
      <div className="srce-cmp-label">{title}</div>
      <div className="srce-cmp-figs">
        {figures.map(([k, v]) => (
          <div key={k}><span className="srce-k">{k}</span><span className="srce-num srce-strong">{formatAmount(v, currency)}</span></div>
        ))}
      </div>
    </div>
  );
}

// The comparison's figures, shared by the Offers tab and the chat card.
export function ComparisonSummary({ summary, builtAt, compact = false }) {
  if (!summary) return null;
  const cur = summary.currency;
  const kp = summary.known_portion || {};
  const inc = summary.incumbents || {};
  const gaps = summary.gaps || {};
  return (
    <div className={`srce-cmp${compact ? ' srce-cmp--compact' : ''}`}>
      {builtAt && <div className="srce-mut">Snapshot of {formatDueAt(builtAt)}.</div>}
      <div className="srce-cmp-cov">Of {Number(summary.lines_total || 0).toLocaleString()} lines in the event:</div>
      <Coverage summary={summary} compact={compact} />
      <MoneyCard title={kp.label} currency={cur} figures={[
        ["Cost at today's price", kp.projected_today],
        ['Cost at the lowest approved offer', kp.at_lowest_approved],
        ['Difference (today minus offer)', kp.difference],
      ]} />
      <MoneyCard title={inc.label} currency={cur} figures={[
        ["Cost at today's price", inc.projected_today],
        ["Cost at today's supplier's offer", inc.at_incumbent_offer],
        ['Difference (today minus offer)', inc.difference],
      ]} />
      {Number(inc.ineligible_lines || 0) > 0 && (
        <div className="srce-cmp-cov">
          {Number(inc.ineligible_lines).toLocaleString()} {Number(inc.ineligible_lines) === 1 ? 'line' : 'lines'} where
          {' '}today's supplier quoted {Number(inc.ineligible_lines) === 1 ? 'is' : 'are'} left out: the quote is not
          {' '}approved or its minimum order is above the volume.
        </div>
      )}
      {Number(inc.not_calculated_lines || 0) > 0 && (
        <div className="srce-cmp-cov">
          {lineWord(inc.not_calculated_lines)} where today's supplier quoted
          {Number(inc.not_calculated_lines) === 1 ? ' is' : ' are'} left out: the line cannot be
          {' '}calculated (check the quantity and unit).
        </div>
      )}
      {summary.event_wide_difference !== null && summary.event_wide_difference !== undefined ? (
        <div className="srce-cmp-cov">
          Event-wide difference (today minus the lowest approved offers):
          {' '}<span className="srce-strong srce-num">{formatAmount(summary.event_wide_difference, cur)}</span>.
          {' '}A comparison, not an award.
        </div>
      ) : (
        <div className="srce-cmp-cov">
          No event-wide total: {lineWord(gaps.lines_without_usable_price_today)}
          {Number(gaps.lines_without_usable_price_today) === 1 ? ' has' : ' have'} no usable price today
          and {lineWord(gaps.lines_without_approved_offer)}
          {Number(gaps.lines_without_approved_offer) === 1 ? ' has' : ' have'} no approved offer
          {Number(gaps.lines_not_calculated || 0) > 0 && (
            <>; {lineWord(gaps.lines_not_calculated)} cannot be calculated (check the quantity and unit)</>
          )}.
        </div>
      )}
    </div>
  );
}

function Offer({ row, currency }) {
  if (!row) return <span className="srce-mut">None</span>;
  return (
    <span>
      <span className="srce-num">{formatUnitPrice(row.price, currency)}</span>
      <span className="srce-sub"> {row.supplier_name || ''}</span>
      {row.lead_time_days !== null && row.lead_time_days !== undefined && (
        <span className="srce-sub">, {row.lead_time_days} days</span>
      )}
    </span>
  );
}

function LinesTable({ lines, currency }) {
  const [page, setPage] = useState(1);
  const ref = useRef(null);
  useEffect(() => { setPage(1); }, [lines]);
  const shown = lines.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  return (
    <div ref={ref}>
      <div className="srce-scroll srce-gap">
        <table className="srce-grid">
          <thead>
            <tr>
              <th>Part</th><th>Site</th><th className="srce-r">Volume</th><th className="srce-r">Today's price</th>
              <th>Today's supplier</th><th className="srce-r">Their offer</th>
              <th>Lowest approved offer</th><th>Lowest conditional offer</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((l, i) => (
              <tr key={`${l.part_number}|${l.site}|${i}`}>
                <td className="srce-mono">{l.part_number}</td>
                <td>{l.site}</td>
                <td className="srce-r srce-num">{formatVolume(l.quantity)} {l.unit || ''}</td>
                <td className="srce-r">
                  {l.today_price !== null && l.today_price !== undefined && !l.today_usable ? (
                    <>
                      <span className="srce-num">{formatOwnPrice(l.today_price, l.today_currency, l.today_unit)}</span>
                      <div className="srce-warn">Not used: different unit or currency</div>
                    </>
                  ) : <span className="srce-num">{formatUnitPrice(l.today_price, currency)}</span>}
                </td>
                <td>{l.incumbent_name || <span className="srce-mut">None</span>}</td>
                <td className="srce-r">
                  <span className="srce-num">{l.incumbent_offer === null || l.incumbent_offer === undefined ? 'None' : formatUnitPrice(l.incumbent_offer, currency)}</span>
                  {l.incumbent_offer_ineligible && (
                    <div className="srce-warn">Not in the incumbent total: not approved, or minimum order above the volume</div>
                  )}
                </td>
                <td>
                  {l.calculated === false
                    ? <span className="srce-warn">Not calculated: check the quantity and unit</span>
                    : <Offer row={l.lowest_approved} currency={currency} />}
                </td>
                <td>
                  <Offer row={l.lowest_conditional} currency={currency} />
                  {(l.min_order_above_volume || []).length > 0 && (
                    <div className="srce-warn" title="These offers are not counted as lowest.">
                      Minimum order above the volume: {l.min_order_above_volume.map((r) => r.supplier_name).join(', ')}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager data={{ total: lines.length, page, page_size: PAGE_SIZE }} onPage={setPage}
        label="lines" numbered scrollTargetRef={ref} />
    </div>
  );
}

export default function ComparisonPanel({ api, eventId, refreshKey, onRebuild, building }) {
  const [data, setData] = useState(undefined);   // undefined while loading
  const [error, setError] = useState('');
  const load = useCallback(() => api.latestComparison(eventId)
    .then((r) => { setData(r.comparison); setError(''); })
    .catch((e) => setError(e.message)), [api, eventId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  // A supplier can submit while this tab is open: re-read the snapshot (the
  // server re-checks whether it is out of date) every minute while the page
  // is visible, and when the buyer comes back to it (Codex 2C round 2).
  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') load(); };
    const timer = setInterval(tick, 60000);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, [load]);

  if (error) return <div className="srce-error" role="alert">{error}</div>;
  if (data === undefined) return <div className="ll-loading">Loading the comparison…</div>;
  if (data === null) {
    return (
      <div className="srce-notice">
        No comparison yet. Compare bids saves a snapshot of today's prices against the offers.
      </div>
    );
  }
  const currency = data.summary && data.summary.currency;
  return (
    <section className="srce-panel" aria-label="Comparison with today's prices">
      <div className="srce-actions">
        <h2 className="srce-h2 srce-h2--flush">How the bids compare to what you pay today</h2>
      </div>
      {data.out_of_date && (
        <div className="srce-warnbox srce-row">
          Out of date: the offers, parts or approvals changed since this snapshot.
          <button type="button" className="srce-btn" disabled={building} onClick={onRebuild}>
            {building ? 'Rebuilding…' : 'Rebuild'}</button>
        </div>
      )}
      <ComparisonSummary summary={data.summary} builtAt={data.built_at} />
      {(data.lines || []).length > 0 && <LinesTable lines={data.lines} currency={currency} />}
    </section>
  );
}
